import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import {
  Globe,
  Image,
  PanelRight,
  Search,
  Sparkles,
  Wrench,
} from "lucide-react";
import {
  ClaudeIcon,
  CohereIcon,
  DeepSeekIcon,
  GeminiIcon,
  GrokIcon,
  GroqIcon,
  MistralIcon,
  OpenAIIcon,
  OpenRouterIcon,
  PerplexityIcon,
  QwenIcon,
} from "../../header/icons";

// Default pulse loader icon component
const PulseLoaderIcon = ({ size = 12, className = "" }) => {
  return (
    <div
      className={cn("rounded-full bg-gray-400 dark:bg-gray-500", className)}
      style={{
        width: `${size}px`,
        height: `${size}px`,
        animation: "providerPulse 2s ease-in-out infinite",
      }}
    />
  );
};

// Provider icon styling function
const getProviderIcon = (provider) => {
  switch (provider) {
    case "deepseek":
      return (
        <DeepSeekIcon
          size={12}
          className="text-[#015482] dark:text-[#015482]"
        />
      );
    case "gemini":
    case "google":
      return (
        <GeminiIcon size={12} className="text-[#1967D2] dark:text-[#1967D2]" />
      );
    case "mistral":
      return (
        <MistralIcon
          size={12}
          className="text-orange-600 dark:text-orange-400"
        />
      );
    case "anthropic":
    case "claude":
      return <ClaudeIcon size={12} className="fill-[#D97757]" />;
    case "groq":
      return (
        <GroqIcon size={12} className="text-green-600 dark:text-green-400" />
      );
    case "xai":
    case "grok":
      return (
        <GrokIcon size={12} className="text-blue-400 dark:text-blue-200" />
      );
    case "cohere":
      return (
        <CohereIcon size={12} className="text-teal-600 dark:text-teal-400" />
      );
    case "perplexity":
      return (
        <PerplexityIcon size={12} className="text-red-600 dark:text-red-400" />
      );
    case "qwen":
      return (
        <QwenIcon size={12} className="text-purple-600 dark:text-purple-400" />
      );
    case "openrouter":
      return (
        <OpenRouterIcon
          size={12}
          className="text-indigo-600 dark:text-indigo-400"
        />
      );
    case "openai":
      return (
        <OpenAIIcon
          size={12}
          className="fill-black dark:fill-white text-black dark:text-white"
        />
      );
    case "loading":
      return <PulseLoaderIcon size={12} />;
    case "unknown":
    default:
      return (
        <div
          className="rounded-full bg-gray-400 dark:bg-gray-500"
          style={{
            width: "12px",
            height: "12px",
          }}
        />
      );
  }
};

// Determine provider from model name
const determineProviderFromModel = (model) => {
  if (!model) return "openai";

  // Check for OpenRouter models which use a slash format
  if (model.includes("/")) {
    const prefix = model.split("/")[0].toLowerCase();
    if (
      prefix === "cognitivecomputations" ||
      prefix === "nousresearch" ||
      prefix === "teknium" ||
      prefix === "migtissera"
    ) {
      return "openrouter";
    }
  }

  // Check if model name includes specific provider names
  if (model.includes("deepseek")) return "deepseek";
  if (model.includes("gemini")) return "gemini";
  if (model.includes("claude")) return "anthropic";
  if (model.includes("qwen")) return "qwen";
  // Check for specific Groq models first before the general mistral check
  if (model === "mistral-large-2402") return "groq";
  if (model.includes("mistral") && !model.includes("mixtral")) return "mistral";
  if (model.includes("grok")) return "xai";
  if (model.includes("command")) return "cohere";
  if (model.includes("sonar") || model.includes("r1-1776")) return "perplexity";
  if (
    model.includes("gemma") ||
    model.includes("llama") ||
    model.includes("mixtral") ||
    (model.includes("mistral") && model.includes("mixtral"))
  )
    return "groq";

  return "openai";
};

// Tool name to display info mapping
const TOOL_DISPLAY_INFO = {
  web_search: { label: "Web Search", icon: Globe },
  firecrawl_search: { label: "Web Search", icon: Globe },
  image_gen: { label: "Image Generation", icon: Image },
  image_generation: { label: "Image Generation", icon: Image },
  image_edit: { label: "Image Edit", icon: Image },
  canvas_create: { label: "Canvas", icon: PanelRight },
  canvas_stream: { label: "Canvas", icon: PanelRight },
  canvas_edit: { label: "Canvas", icon: PanelRight },
  deep_research: { label: "Deep Research", icon: Search },
  code_interpreter: { label: "Code Interpreter", icon: Sparkles },
};

// Extract tool provider from tool result if available
const extractToolProvider = (toolCall) => {
  if (!toolCall?.result) return null;

  const result = toolCall.result;

  // Check for provider in result metadata
  if (result.provider) return result.provider;
  if (result.metadata?.provider) return result.metadata.provider;

  // Check for provider in nested result structures
  if (result.result?.provider) return result.result.provider;
  if (result.result?.metadata?.provider) return result.result.metadata.provider;

  // For web search, check if it's from Firecrawl or Perplexity
  const toolName = toolCall.function?.name || toolCall.name;
  if (toolName === "web_search" || toolName === "firecrawl_search") {
    // Check result structure to determine provider
    if (result.data && Array.isArray(result.data)) {
      // Firecrawl typically returns structured data
      return "firecrawl";
    }
    // Perplexity citations are usually in metadata
    return "perplexity";
  }

  // For image generation, check for DALL-E or other providers
  if (toolName === "image_gen" || toolName === "image_generation") {
    if (result.provider) return result.provider;
    // DALL-E is default for image generation
    return "dalle";
  }

  return null;
};

// Extract tools used from message (and optionally previous message) with execution order
const extractToolsWithOrder = (message, previousMessage = null) => {
  const toolItems = [];
  const seenToolNames = new Set();

  // Check for explicit tool_calls in current message
  let toolCalls = message?.tool_calls || message?.metadata?.tool_calls;

  if (toolCalls) {
    // Parse if string
    if (typeof toolCalls === "string") {
      try {
        toolCalls = JSON.parse(toolCalls);
      } catch (e) {
        toolCalls = [];
      }
    }

    if (Array.isArray(toolCalls) && toolCalls.length > 0) {
      toolCalls.forEach((call) => {
        const toolName = call.function?.name || call.name;
        if (toolName && !seenToolNames.has(toolName)) {
          seenToolNames.add(toolName);
          toolItems.push({
            name: toolName,
            provider: extractToolProvider(call),
            order: toolItems.length + 1,
          });
        }
      });
    }
  }

  // If current message doesn't have tool_calls but has content,
  // check the previous message for tool_calls (follow-up content scenario)
  if (toolItems.length === 0 && previousMessage) {
    let prevToolCalls =
      previousMessage?.tool_calls || previousMessage?.metadata?.tool_calls;

    if (prevToolCalls) {
      // Parse if string
      if (typeof prevToolCalls === "string") {
        try {
          prevToolCalls = JSON.parse(prevToolCalls);
        } catch (e) {
          prevToolCalls = [];
        }
      }

      if (Array.isArray(prevToolCalls) && prevToolCalls.length > 0) {
        prevToolCalls.forEach((call) => {
          const toolName = call.function?.name || call.name;
          if (toolName && !seenToolNames.has(toolName)) {
            seenToolNames.add(toolName);
            toolItems.push({
              name: toolName,
              provider: extractToolProvider(call),
              order: toolItems.length + 1,
            });
          }
        });
      }
    }
  }

  // Check for Perplexity citations (indicates web search was used)
  // Check both current and previous message
  const messagesToCheck = [message, previousMessage].filter(Boolean);
  for (const msg of messagesToCheck) {
    if (
      msg?.metadata?.citations &&
      Array.isArray(msg.metadata.citations) &&
      msg.metadata.citations.length > 0
    ) {
      if (!seenToolNames.has("web_search")) {
        seenToolNames.add("web_search");
        toolItems.push({
          name: "web_search",
          provider: "perplexity",
          order: toolItems.length + 1,
        });
      }
      break; // Only add once
    }
  }

  // Check for deep research indicators
  if (
    message?.metadata?.isDeepResearch ||
    message?.metadata?.isDeepResearchResult ||
    message?.metadata?.isDeepResearchInProgress ||
    message?.metadata?.deepResearch
  ) {
    if (!seenToolNames.has("deep_research")) {
      seenToolNames.add("deep_research");
      toolItems.push({
        name: "deep_research",
        provider: null, // Deep research doesn't have a separate provider
        order: toolItems.length + 1,
      });
    }
  }

  return toolItems;
};

// Format model name for display (make it more readable)
const formatModelName = (model) => {
  if (!model) return null;

  // Return the model name as-is for now, it's already pretty readable
  // Could add more formatting logic here if needed
  return model;
};

// Extract token usage from message metadata
const extractTokenUsage = (message, previousMessage = null) => {
  // Check current message first
  const usage = message?.metadata?.usage || message?.metadata?.tokenUsage;

  if (usage) {
    // Handle different usage formats
    const input =
      usage.prompt_tokens || usage.input_tokens || usage.promptTokenCount || 0;
    const output =
      usage.completion_tokens ||
      usage.output_tokens ||
      usage.candidatesTokenCount ||
      0;

    if (input > 0 || output > 0) {
      return {
        input,
        output,
        total: usage.total_tokens || usage.totalTokenCount || input + output,
      };
    }
  }

  // Check previous message if current doesn't have usage
  if (previousMessage) {
    const prevUsage =
      previousMessage?.metadata?.usage || previousMessage?.metadata?.tokenUsage;
    if (prevUsage) {
      const input =
        prevUsage.prompt_tokens ||
        prevUsage.input_tokens ||
        prevUsage.promptTokenCount ||
        0;
      const output =
        prevUsage.completion_tokens ||
        prevUsage.output_tokens ||
        prevUsage.candidatesTokenCount ||
        0;

      if (input > 0 || output > 0) {
        return {
          input,
          output,
          total:
            prevUsage.total_tokens ||
            prevUsage.totalTokenCount ||
            input + output,
        };
      }
    }
  }

  return null;
};

// Format number with commas
const formatNumber = (num) => {
  if (num === null || num === undefined) return "0";
  return num.toLocaleString();
};

// Calculate query/response time from message timestamps
const calculateQueryTime = (message, previousMessage = null) => {
  // Try to get elapsed time from metadata first
  if (message?.metadata?.elapsedTime) {
    return formatDuration(message.metadata.elapsedTime);
  }

  if (message?.metadata?.duration) {
    return formatDuration(message.metadata.duration);
  }

  // Calculate from timestamps
  const startTime = message?.created_at || message?.metadata?.timestamp;
  const endTime = message?.updated_at || message?.metadata?.completedAt;

  if (startTime && endTime) {
    try {
      const start = new Date(startTime);
      const end = new Date(endTime);
      const diffMs = end - start;

      if (diffMs > 0) {
        return formatDuration(diffMs);
      }
    } catch (e) {
      // Invalid date, skip
    }
  }

  // If current message doesn't have timing, check previous message
  if (previousMessage) {
    if (previousMessage?.metadata?.elapsedTime) {
      return formatDuration(previousMessage.metadata.elapsedTime);
    }

    const prevStartTime =
      previousMessage?.created_at || previousMessage?.metadata?.timestamp;
    const prevEndTime =
      previousMessage?.updated_at || previousMessage?.metadata?.completedAt;

    if (prevStartTime && prevEndTime) {
      try {
        const start = new Date(prevStartTime);
        const end = new Date(prevEndTime);
        const diffMs = end - start;

        if (diffMs > 0) {
          return formatDuration(diffMs);
        }
      } catch (e) {
        // Invalid date, skip
      }
    }
  }

  return null;
};

// Format duration in milliseconds to human-readable string
const formatDuration = (ms) => {
  // If it's already in seconds, convert to ms
  const milliseconds = ms < 1000 ? ms * 1000 : ms;

  if (milliseconds < 1000) {
    return `${Math.round(milliseconds)}ms`;
  }

  const seconds = milliseconds / 1000;

  if (seconds < 60) {
    return `${seconds.toFixed(1)}s`;
  }

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);

  if (minutes < 60) {
    return `${minutes}m ${remainingSeconds}s`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  return `${hours}h ${remainingMinutes}m`;
};

// Provider Avatar Component
export const ProviderAvatar = ({
  message,
  previousMessage = null,
  isStreaming,
  hasContent,
}) => {
  // PRIORITY 1: Always determine provider from model if available (most accurate for reruns)
  const modelString = message?.metadata?.model || message?.model;
  let provider = null;

  if (modelString) {
    // Determine provider from model string - this ensures correct provider after model rerun
    provider = determineProviderFromModel(modelString);
  }

  // PRIORITY 2: Fallback to explicit provider fields only if model is not available
  if (!provider || provider === "openai") {
    const explicitProvider = message?.metadata?.provider || message?.provider;
    if (explicitProvider) {
      provider = explicitProvider;
    }
  }

  // PRIORITY 3: If still no provider, use "loading" to show pulse loader
  if (!provider) {
    provider = "loading";
  }

  // Normalize provider name
  provider = provider.toLowerCase();

  // Override provider to "loading" only if actually streaming
  // This prevents old messages from continuing to pulse
  if (provider === "loading" && !isStreaming) {
    provider = "unknown"; // Use unknown instead of loading to avoid pulsing
  }

  // Capitalize provider name for display
  const providerDisplayName =
    provider === "loading"
      ? "Loading..."
      : provider === "unknown"
        ? "Assistant"
        : provider.charAt(0).toUpperCase() + provider.slice(1);

  // Extract model name
  const modelName = formatModelName(message?.metadata?.model || message?.model);

  // Extract tools with execution order (check both current and previous message)
  const toolsWithOrder = extractToolsWithOrder(message, previousMessage);

  // Extract token usage
  const tokenUsage = extractTokenUsage(message, previousMessage);

  // Calculate query time
  const queryTime = calculateQueryTime(message, previousMessage);

  // Get display info for tools with order preserved
  const toolDisplayItems = toolsWithOrder
    .map((toolItem) => {
      const info = TOOL_DISPLAY_INFO[toolItem.name];
      if (info) {
        return {
          ...toolItem,
          ...info,
        };
      }
      // Fallback for unknown tools
      return {
        ...toolItem,
        label: toolItem.name
          .replace(/_/g, " ")
          .replace(/\b\w/g, (l) => l.toUpperCase()),
        icon: Wrench,
      };
    })
    // Remove duplicates by label (e.g., canvas_create and canvas_stream both show as "Canvas")
    // But preserve the first occurrence to maintain order
    .filter((item, index, self) => {
      const firstIndex = self.findIndex((t) => t.label === item.label);
      if (firstIndex !== index) {
        // If this is a duplicate, merge provider info if available
        if (item.provider && !self[firstIndex].provider) {
          self[firstIndex].provider = item.provider;
        }
        return false;
      }
      return true;
    });

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={cn(
              "w-5 h-5 rounded-full flex items-center justify-center",
              "bg-transparent border border-gray-300 dark:border-gray-500",
              "shadow-sm hover:shadow-md transition-all duration-300 cursor-default flex-shrink-0 mr-2"
            )}
          >
            {getProviderIcon(provider)}
          </div>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          className="TooltipContent max-w-xs"
          sideOffset={5}
        >
          <div className="space-y-1.5">
            {/* Provider name */}
            <div className="font-medium text-sm">{providerDisplayName}</div>

            {/* Model name */}
            {modelName && !isStreaming && (
              <div className="text-xs text-muted-foreground flex items-center gap-1.5">
                <span className="opacity-60">Model:</span>
                <span className="font-mono text-[11px]">{modelName}</span>
              </div>
            )}

            {/* Token Usage */}
            {tokenUsage && !isStreaming && (
              <div className="text-xs text-muted-foreground border-t pt-1.5 mt-1.5">
                <div className="flex justify-between gap-4">
                  <span>
                    <span className="opacity-60">Input:</span>{" "}
                    <span className="font-mono text-[11px]">
                      {formatNumber(tokenUsage.input)}
                    </span>
                  </span>
                  <span>
                    <span className="opacity-60">Output:</span>{" "}
                    <span className="font-mono text-[11px]">
                      {formatNumber(tokenUsage.output)}
                    </span>
                  </span>
                </div>
              </div>
            )}

            {/* Query Time */}
            {queryTime && !isStreaming && (
              <div className="text-xs text-muted-foreground">
                <span className="opacity-60">Time:</span>{" "}
                <span className="font-mono text-[11px]">{queryTime}</span>
              </div>
            )}

            {/* Tools used with execution order */}
            {toolDisplayItems.length > 0 && !isStreaming && (
              <div className="text-xs text-muted-foreground border-t pt-1.5 mt-1.5">
                <div className="opacity-60 mb-1.5">
                  Tools ({toolDisplayItems.length}):
                </div>
                <div className="space-y-1">
                  {toolDisplayItems.map((tool) => {
                    const IconComponent = tool.icon;
                    return (
                      <div
                        key={`${tool.name}-${tool.order}`}
                        className="flex items-center gap-1.5"
                      >
                        <span className="text-[10px] opacity-50 font-mono min-w-[12px]">
                          {tool.order}.
                        </span>
                        <IconComponent className="w-3 h-3 flex-shrink-0" />
                        <span className="flex-1">{tool.label}</span>
                        {tool.provider && (
                          <span className="text-[10px] opacity-50 capitalize">
                            ({tool.provider})
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Tool Selection Context (Debug) */}
            {message?.metadata?.toolSelectionContext && !isStreaming && (
              <div className="text-xs text-muted-foreground border-t pt-1.5 mt-1.5">
                <div className="opacity-60 mb-1">Tool Selection:</div>
                <div className="space-y-0.5 text-[10px]">
                  <div>
                    Explicit:{" "}
                    {message.metadata.toolSelectionContext
                      .hasExplicitToolSelection
                      ? "Yes"
                      : "No"}
                  </div>
                  <div>
                    tool_choice:{" "}
                    {typeof message.metadata.toolSelectionContext
                      .tool_choice === "string"
                      ? message.metadata.toolSelectionContext.tool_choice
                      : JSON.stringify(
                          message.metadata.toolSelectionContext.tool_choice
                        )}
                  </div>
                  {message.metadata.toolSelectionContext.forceWebSearchTool && (
                    <div className="text-green-500">Web Search: Forced</div>
                  )}
                  {message.metadata.toolSelectionContext.forceImageGenTool && (
                    <div className="text-green-500">Image Gen: Forced</div>
                  )}
                  {message.metadata.toolSelectionContext.forceCanvasCreate && (
                    <div className="text-green-500">Canvas: Forced</div>
                  )}
                  {message.metadata.toolSelectionContext.forceImageEditTool && (
                    <div className="text-green-500">Image Edit: Forced</div>
                  )}
                </div>
              </div>
            )}

            {/* Debug: Show metadata keys if toolSelectionContext is missing */}
            {import.meta.env.DEV &&
              !isStreaming &&
              message?.metadata &&
              !message?.metadata?.toolSelectionContext && (
                <div className="text-xs text-muted-foreground border-t pt-1.5 mt-1.5">
                  <div className="opacity-60 mb-1">Debug: Metadata Keys:</div>
                  <div className="text-[10px] font-mono">
                    {Object.keys(message.metadata).join(", ")}
                  </div>
                </div>
              )}

            {/* Streaming indicator */}
            {isStreaming && (
              <div className="text-xs text-muted-foreground italic">
                Generating...
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default ProviderAvatar;
